import { useEffect, useState } from 'react';
import { listPurchaseRequestLines } from '../api/purchasing';
import { listItems, listUoms } from '../api/masterData';
import { useBranch } from '../context/BranchContext';
import { BusinessText, Drawer, DrawerField, EmptyState, ErrorNote, LoadingNote, StatusBadge, formatDateTime, tableStyle, tdStyle, thStyle } from './common';
import type { Item, PurchaseRequest, PurchaseRequestLine, Uom } from '../types/domain';

/**
 * Read-only Purchase Request detail drawer — reused by every screen that
 * lists PR rows (Purchase Requests page, Executive/Manager/Purchasing
 * dashboards) so "No. PR" is a real, inspectable document everywhere it
 * appears instead of only on one page. Self-fetches lines/items/UOMs when
 * opened so call sites don't each need to preload master data just to show
 * a drawer.
 */
export function PrDetailDrawer({ pr, open, onClose }: { pr: PurchaseRequest | null; open: boolean; onClose: () => void }) {
  const { branches } = useBranch();
  const [lines, setLines] = useState<PurchaseRequestLine[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pr || !open) return;
    setLines(null);
    setError(null);
    Promise.all([listPurchaseRequestLines(pr.pr_id), listItems(), listUoms()])
      .then(([lineRows, itemRows, uomRows]) => {
        setLines(lineRows);
        setItems(itemRows);
        setUoms(uomRows);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [pr, open]);

  const itemLabel = (id: string) => {
    const it = items.find((i) => i.item_id === id);
    return it ? `${it.item_code} — ${it.name}` : id;
  };
  const uomLabel = (id: string) => uoms.find((u) => u.uom_id === id)?.code ?? id;
  const branchLabel = (id: string) => branches.find((b) => b.branch_id === id)?.name ?? id;

  return (
    <Drawer open={open && !!pr} onClose={onClose} title={pr ? `PR ${pr.pr_number}` : 'Detail PR'}>
      {pr && (
        <div>
          <DrawerField label="No. PR" value={pr.pr_number} />
          <DrawerField label="Status" value={<StatusBadge status={pr.status} />} />
          <DrawerField label="Cabang" value={branchLabel(pr.branch_id)} />
          <DrawerField label="Tanggal dibutuhkan" value={pr.needed_date ?? '—'} />
          <DrawerField label="Catatan" value={<BusinessText text={pr.notes} />} />
          <DrawerField label="Diajukan" value={formatDateTime(pr.created_at)} />

          <div style={{ marginTop: 16, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-secondary)', marginBottom: 6 }}>Baris PR</div>
          {error && <ErrorNote text={error} />}
          {!error && lines === null && <LoadingNote text="Memuat baris PR..." />}
          {lines && lines.length === 0 && <EmptyState text="Tidak ada baris pada PR ini." />}
          {lines && lines.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>Qty diminta</th>
                  <th style={thStyle}>UOM</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.pr_line_id}>
                    <td style={tdStyle}>{itemLabel(l.item_id)}</td>
                    <td style={tdStyle}>{l.qty_requested}</td>
                    <td style={tdStyle}>{uomLabel(l.uom_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Drawer>
  );
}
